'use client';

import { usePathname } from '@/i18n/navigation';
import {
  markPromotionSeenThisSession,
  queuePromotion,
  wasPromotionSeenThisSession,
} from '@/utils/gtm/promotion-impressions';
import { useEffect, useRef } from 'react';

// Spec trigger: at least 50% of the creative inside the viewport for 2 seconds
// or more.
const VISIBLE_RATIO = 0.5;
const DWELL_MS = 2000;

// Spec §2: never double-fire. Keyed by promotion_id rather than by element, so
// the marquee's repeated ticker items and Swiper's looped slide clones — which
// are separate DOM nodes for the same creative — only produce one impression.
let seenPromotions = new Set();
let seenOnPath = null;

/**
 * Observes a banner and counts a `view_promotion` impression once it has been
 * half visible for the dwell time. Qualified creatives are batched into one push
 * by `promotion-impressions.js`. Returns the ref to put on the element.
 *
 * `oncePerSession` is for creatives that live in the layout rather than on a
 * page — the ticker — which would otherwise report again on every navigation.
 */
export default function usePromotionImpression(
  promotion,
  { oncePerSession = false } = {}
) {
  const ref = useRef(null);
  const latest = useRef(promotion);
  const pathname = usePathname();
  const promotionId = promotion?.promotion_id;

  latest.current = promotion;

  useEffect(() => {
    const node = ref.current;
    if (!node || !promotionId || typeof IntersectionObserver === 'undefined')
      return;

    // Impressions are counted once per page view, so a route change makes every
    // creative eligible again.
    if (seenOnPath !== pathname) {
      seenPromotions = new Set();
      seenOnPath = pathname;
    }
    if (seenPromotions.has(promotionId)) return;
    if (oncePerSession && wasPromotionSeenThisSession(promotionId)) return;

    let timer = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
          if (timer) return;
          timer = setTimeout(() => {
            timer = null;
            if (seenPromotions.has(promotionId)) return;
            if (oncePerSession) {
              if (wasPromotionSeenThisSession(promotionId)) return;
              markPromotionSeenThisSession(promotionId);
            }
            seenPromotions.add(promotionId);
            queuePromotion(latest.current);
            observer.disconnect();
          }, DWELL_MS);
          return;
        }
        // Scrolled back out before the dwell time elapsed — no impression.
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0, VISIBLE_RATIO, 1] }
    );

    observer.observe(node);

    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [promotionId, pathname, oncePerSession]);

  return ref;
}
